import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  Users, 
  Package, 
  DollarSign, 
  TrendingUp,
  Calendar,
  Download,
  RefreshCw,
  Lock,
  Eye,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Sunjin deduction types
const DEDUCTION_TYPES = [
  { key: 'freightPerSack', label: 'Freight & Handling/Sack', color: 'bg-orange-100 text-orange-700' },
  { key: 'freightPerUnit', label: 'Freight & Handling/Kg', color: 'bg-orange-100 text-orange-700' },
  { key: 'srIncentivePerSack', label: 'SR Incentives/Sack', color: 'bg-purple-100 text-purple-700' },
  { key: 'dealerDiscountPerSack', label: 'Dealer Discount/Sack', color: 'bg-blue-100 text-blue-700' },
  { key: 'cashDiscountPerSack', label: 'Cash Discount/Sack', color: 'bg-green-100 text-green-700' },
  { key: 'dealerRebatePerSack', label: 'Dealer Rebate/Sack', color: 'bg-teal-100 text-teal-700' },
  { key: 'delivery', label: 'Delivery', color: 'bg-indigo-100 text-indigo-700' },
  { key: 'marketingFund', label: 'Marketing Fund', color: 'bg-pink-100 text-pink-700' },
  { key: 'srMotorPlan', label: 'SR Motor Plan', color: 'bg-amber-100 text-amber-700' },
  { key: 'manpowerIncentives', label: 'Manpower Incentives', color: 'bg-red-100 text-red-700' }
];

const NASSunjinMonitor = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState({});

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch products (Sunjin only)
      const productsRes = await axios.get(`${API}/api/nas/products`);
      const sunjinProducts = productsRes.data.filter(p => 
        p.supplierName?.toLowerCase().includes('sunjin')
      );
      setProducts(sunjinProducts);

      // Fetch all users with NAS access
      const usersRes = await axios.get(`${API}/api/users`);
      const nasUsers = usersRes.data.filter(u => u.nas_access);
      setUsers(nasUsers);

      // Fetch orders/transactions
      try {
        const ordersRes = await axios.get(`${API}/api/nas/transactions`);
        setOrders(ordersRes.data || []);
      } catch (err) {
        console.log('No transactions endpoint, using empty array');
        setOrders([]);
      }

      // Calculate summary
      calculateSummary(sunjinProducts, nasUsers);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (prods, usrs) => {
    // Calculate total deductions from all Sunjin products
    const totalDeductions = {};
    DEDUCTION_TYPES.forEach(d => {
      totalDeductions[d.key] = prods.reduce((sum, p) => sum + (p[d.key] || 0), 0);
    });

    // Calculate user incentives
    const userIncentives = usrs.reduce((sum, u) => sum + (u.sr_incentives_total || 0), 0);

    setSummary({
      totalProducts: prods.length,
      totalUsers: usrs.length,
      totalDeductions,
      userIncentives
    });
  };

  // Admin-only access check
  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <Lock size={40} className="text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
        <p className="text-slate-600 text-center">
          This page is restricted to administrators only.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="sunjin-monitor-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 size={28} className="text-blue-600" />
            Sunjin Deductions Monitor
            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
              ADMIN ONLY
            </span>
          </h1>
          <p className="text-slate-600 mt-1">Track all Sunjin product deductions and user incentives</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw size={16} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Package size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium">Sunjin Products</p>
                <p className="text-2xl font-bold text-blue-800">{summary.totalProducts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-600 rounded-lg">
                <Users size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-purple-600 font-medium">NAS Users</p>
                <p className="text-2xl font-bold text-purple-800">{summary.totalUsers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-600 rounded-lg">
                <DollarSign size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-green-600 font-medium">Total User Incentives</p>
                <p className="text-2xl font-bold text-green-800">₱{(summary.userIncentives || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-600 rounded-lg">
                <TrendingUp size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-orange-600 font-medium">Total SR Incentive Rate</p>
                <p className="text-2xl font-bold text-orange-800">
                  ₱{(summary.totalDeductions?.srIncentivePerSack || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deductions Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye size={20} className="text-slate-600" />
            Sunjin Deductions Summary (Per Product Rates)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {DEDUCTION_TYPES.map(deduction => (
              <div 
                key={deduction.key} 
                className={`p-3 rounded-lg border ${deduction.color}`}
              >
                <p className="text-xs font-medium opacity-80">{deduction.label}</p>
                <p className="text-lg font-bold">
                  ₱{(summary.totalDeductions?.[deduction.key] || 0).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Users with SR Incentives */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users size={20} className="text-purple-600" />
            Users SR Incentives Tracking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">SR Enabled</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Rate/Sack</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Total Earned</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.length > 0 ? users.map(u => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{u.full_name || u.username}</p>
                      <p className="text-xs text-slate-500">{u.mobile}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.sr_incentives_enabled ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Yes</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-500">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-purple-600">
                      ₱{(u.sr_incentives_rate || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 font-bold text-green-600">
                      ₱{(u.sr_incentives_total || 0).toLocaleString()}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                      No NAS users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Sunjin Products with Deductions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package size={20} className="text-blue-600" />
            Sunjin Products - Deduction Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Product</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Freight/Sack</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">SR Incentive</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Dealer Disc</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Cash Disc</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Rebate</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Delivery</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Marketing</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Motor Plan</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Manpower</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600 bg-slate-100">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {products.length > 0 ? products.map(product => {
                  const totalDeduction = (
                    (product.freightPerSack || 0) +
                    (product.handlingPerSack || 0) +
                    (product.srIncentivePerSack || 0) +
                    (product.dealerDiscountPerSack || 0) +
                    (product.cashDiscountPerSack || 0) +
                    (product.dealerRebatePerSack || 0) +
                    (product.delivery || 0) +
                    (product.marketingFund || 0) +
                    (product.srMotorPlan || 0) +
                    (product.manpowerIncentives || 0)
                  );
                  return (
                    <tr key={product.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <p className="font-medium text-slate-900">{product.name}</p>
                        <p className="text-xs text-slate-500">{product.category}</p>
                      </td>
                      <td className="px-3 py-2 text-right text-orange-600">
                        ₱{((product.freightPerSack || 0) + (product.handlingPerSack || 0)).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-purple-600">
                        ₱{(product.srIncentivePerSack || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-blue-600">
                        ₱{(product.dealerDiscountPerSack || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-green-600">
                        ₱{(product.cashDiscountPerSack || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-teal-600">
                        ₱{(product.dealerRebatePerSack || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-indigo-600">
                        ₱{(product.delivery || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-pink-600">
                        ₱{(product.marketingFund || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-amber-600">
                        ₱{(product.srMotorPlan || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-red-600">
                        ₱{(product.manpowerIncentives || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-slate-900 bg-slate-50">
                        ₱{totalDeduction.toFixed(2)}
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="11" className="px-4 py-8 text-center text-slate-500">
                      No Sunjin products found. Add products with "Sunjin" as supplier name.
                    </td>
                  </tr>
                )}
              </tbody>
              {products.length > 0 && (
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr>
                    <td className="px-3 py-2 font-bold text-slate-900">TOTALS</td>
                    <td className="px-3 py-2 text-right font-bold text-orange-700">
                      ₱{products.reduce((s, p) => s + (p.freightPerSack || 0) + (p.handlingPerSack || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-purple-700">
                      ₱{products.reduce((s, p) => s + (p.srIncentivePerSack || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-blue-700">
                      ₱{products.reduce((s, p) => s + (p.dealerDiscountPerSack || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-green-700">
                      ₱{products.reduce((s, p) => s + (p.cashDiscountPerSack || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-teal-700">
                      ₱{products.reduce((s, p) => s + (p.dealerRebatePerSack || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-indigo-700">
                      ₱{products.reduce((s, p) => s + (p.delivery || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-pink-700">
                      ₱{products.reduce((s, p) => s + (p.marketingFund || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-amber-700">
                      ₱{products.reduce((s, p) => s + (p.srMotorPlan || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-red-700">
                      ₱{products.reduce((s, p) => s + (p.manpowerIncentives || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900 bg-yellow-100">
                      ₱{products.reduce((s, p) => {
                        return s + (p.freightPerSack || 0) + (p.handlingPerSack || 0) +
                          (p.srIncentivePerSack || 0) + (p.dealerDiscountPerSack || 0) +
                          (p.cashDiscountPerSack || 0) + (p.dealerRebatePerSack || 0) +
                          (p.delivery || 0) + (p.marketingFund || 0) +
                          (p.srMotorPlan || 0) + (p.manpowerIncentives || 0);
                      }, 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NASSunjinMonitor;
